import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { getName } from '@pletly/shared';
import {
	CancelIcon,
	ContactItem,
	FlashList,
	Modal,
	ModalProps,
	Text,
	Toast,
	useConfirm,
} from '@pletly/ui';
import { useAuthHeaders } from '@pletly/feature-auth';
import { OrganisationMemberDataType } from '../../OrganisationMembersTable';
import { ActionButton } from '../../../../invitations/components/InvitationsFromFamilyCaregivers/ActionButton';
import { useDisconnectCareGiverMutation } from './__generated__/disconnectCaregiver.generated';
import { GetOrganisationDocument } from '../../__generated__/organisation.generated';
import { useGoToProfile } from '../../../../components/useGoToProfile';

interface Props extends ModalProps {
	caregiver?: OrganisationMemberDataType;
	onDisconnectSuccess?: ConnectionItemProps['onDisconnectSuccess'];
}

export const FamilyCaregiverConnectionsModal = ({
	visible,
	onClose,
	caregiver,
	onDisconnectSuccess,
}: Props) => {
	const { t } = useTranslation();
	const goToProfile = useGoToProfile();

	const handleRowPress = useCallback(
		(careRecipientId: string) => () => {
			onClose?.();
			goToProfile({ id: careRecipientId });
		},
		[onClose, goToProfile],
	);

	if (!caregiver) {
		return null;
	}

	return (
		<Modal
			title={t('family-caregiver-connections-modal.title')}
			visible={visible}
			onClose={onClose}
		>
			<FlashList
				estimatedItemSize={64}
				ListEmptyComponent={
					<Text align="center">
						{t('family-caregiver-connections-modal.connections-list-empty')}
					</Text>
				}
				data={caregiver.guardianOf}
				renderItem={({ item: careRecipient }) => (
					<ConnectionItem
						caregiver={caregiver}
						careRecipient={careRecipient}
						onPress={handleRowPress(careRecipient.id)}
						onDisconnectSuccess={onDisconnectSuccess}
					/>
				)}
			/>
		</Modal>
	);
};

interface ConnectionItemProps {
	caregiver: OrganisationMemberDataType;
	careRecipient: OrganisationMemberDataType;
	onPress?: () => unknown;
	onDisconnectSuccess?: ({
		caregiverUuid,
		careRecipientUuid,
	}: {
		caregiverUuid: string;
		careRecipientUuid: string;
	}) => unknown;
}

const ConnectionItem = ({
	caregiver,
	careRecipient,
	onPress,
	onDisconnectSuccess,
}: ConnectionItemProps) => {
	const { t } = useTranslation();
	const { getHeaders } = useAuthHeaders();
	const { confirm } = useConfirm();
	const [disconnectCaregiver, { loading }] = useDisconnectCareGiverMutation({
		refetchQueries: [GetOrganisationDocument],
	});

	const handleDisconnect = useCallback(
		async ({
			caregiverUuid,
			careRecipientUuid,
		}: {
			caregiverUuid: string;
			careRecipientUuid: string;
		}) => {
			try {
				await disconnectCaregiver({
					variables: { caregiverUuid, careRecipientUuid },
				});

				onDisconnectSuccess?.({ caregiverUuid, careRecipientUuid });
				Toast.show({
					text1: t('toasts.success.family-caregiver-disconnected'),
				});
			} catch (error) {
				Toast.show({
					text1: t('toasts.error.something-wrong'),
					type: 'error',
				});
			}
		},
		[disconnectCaregiver, onDisconnectSuccess, t],
	);

	const handleDisconnectPress = useCallback(
		({
			caregiver,
			careRecipient,
		}: {
			caregiver: OrganisationMemberDataType;
			careRecipient: OrganisationMemberDataType;
		}) =>
			async () => {
				if (loading) {
					return;
				}

				const caregiverName = getName(caregiver);
				const careRecipientName = getName(careRecipient);
				const confirmation = await confirm(
					t('family-caregiver-connections-modal.confirm-disconnect.title'),
					t('family-caregiver-connections-modal.confirm-disconnect.content', {
						caregiverName,
						careRecipientName,
					}),
				);

				if (!confirmation) {
					return;
				}

				await handleDisconnect({
					caregiverUuid: caregiver.uuid,
					careRecipientUuid: careRecipient.uuid,
				});
			},
		[confirm, handleDisconnect, loading, t],
	);

	const { photoUrl } = careRecipient;

	return (
		<ContactItem
			name={getName(careRecipient)}
			photoSource={
				photoUrl
					? {
							uri: photoUrl,
							headers: getHeaders(photoUrl),
						}
					: undefined
			}
			action={
				<ActionButton
					onPress={handleDisconnectPress({ caregiver, careRecipient })}
					Icon={CancelIcon}
					label={t('family-caregiver-connections-modal.button-disconnect')}
					color="danger"
					loading={loading}
					disabled={loading}
				/>
			}
			onPress={onPress}
			disabled={loading}
		/>
	);
};
